import React, { useEffect } from "react";
import { Button, Input, Popover, Tooltip } from "antd";
import {
  PrinterOutlined,
  SendOutlined,
  WhatsAppOutlined,
  MessageOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { SHORTCUTS } from "../../Constant";

export interface InvoiceDrawerFooterProps {
  // Action Handlers
  onPrint?: () => void;
  onDownload?: () => void;
  onCourier?: () => void;
  onClose?: () => void;

  // Backward compatibility alias handlers
  onPrintInvoice?: () => void;
  onDownloadInvoice?: () => void;
  onOpenCourier?: () => void;

  // Visibility Flags
  showPrint?: boolean;
  showDownload?: boolean;
  showCourier?: boolean;
  showClose?: boolean;
  showShare?: boolean;
  showShortcuts?: boolean;
  useSharePopover?: boolean;

  // Custom Extra Elements
  extraActions?: React.ReactNode;
  footerNote?: React.ReactNode;

  // WhatsApp & SMS Sharing
  whatsappPhone?: string;
  defaultPhone?: string;
  onWhatsappPhoneChange?: (phone: string) => void;
  onSendWhatsApp?: () => void;
  onSendWhatsAppLink?: () => void;
  onSendSMSLink?: () => void;
  isSendWhatsAppPending?: boolean;
  isSendWhatsAppLinkPending?: boolean;
  isSendSmsLinkPending?: boolean;
  isWhatsAppEnabled?: boolean;
  isWhatsAppConnected?: boolean;
  isSmsGatewayEnable?: boolean;
}

export const InvoiceDrawerFooter: React.FC<InvoiceDrawerFooterProps> = ({
  onPrint,
  onDownload,
  onCourier,
  onPrintInvoice,
  onDownloadInvoice,
  onOpenCourier,
  showPrint,
  showDownload,
  showCourier,
  showShare,
  useSharePopover = false,
  extraActions,
  whatsappPhone = "",
  defaultPhone = "",
  onWhatsappPhoneChange,
  onSendWhatsApp,
  onSendWhatsAppLink,
  onSendSMSLink,
  isSendWhatsAppPending = false,
  isSendWhatsAppLinkPending = false,
  isSendSmsLinkPending = false,
  isWhatsAppEnabled = true,
  isWhatsAppConnected = true,
  isSmsGatewayEnable = true,
}) => {
  const handlePrint = onPrint || onPrintInvoice;
  const handleDownload = onDownload || onDownloadInvoice;
  const handleCourier = onCourier || onOpenCourier;

  const canPrint = showPrint !== undefined ? showPrint : !!handlePrint;
  const canDownload =
    showDownload !== undefined ? showDownload : !!handleDownload;
  const canCourier = showCourier !== undefined ? showCourier : !!handleCourier;

  const hasShareHandlers = !!(
    onSendWhatsApp ||
    onSendWhatsAppLink ||
    onSendSMSLink
  );
  const canShare = showShare !== undefined ? showShare : hasShareHandlers;

  const effectivePhone = (whatsappPhone || defaultPhone || "").trim();

  useEffect(() => {
    if (!whatsappPhone && defaultPhone && onWhatsappPhoneChange) {
      onWhatsappPhoneChange(defaultPhone);
    }
  }, [whatsappPhone, defaultPhone, onWhatsappPhoneChange]);

  const getWhatsAppDisabledReason = () => {
    if (!isWhatsAppEnabled) {
      return "WhatsApp is disabled in System Config.";
    }
    if (!isWhatsAppConnected) {
      return "WhatsApp Gateway is not connected. Connect it in System -> WhatsApp Chatbot.";
    }
    return undefined;
  };

  const whatsappDisabledTooltip = getWhatsAppDisabledReason();
  const isWhatsAppButtonDisabled = !isWhatsAppEnabled || !isWhatsAppConnected;

  const sharePopoverContent = (
    <div className="flex flex-col gap-2 p-1 min-w-[260px] max-w-[320px]">
      <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        Share Invoice / Link
      </div>
      <Input
        placeholder="Phone (e.g. 0712345678)"
        prefix={<SendOutlined className="text-gray-400" />}
        value={effectivePhone}
        onChange={(e) => onWhatsappPhoneChange?.(e.target.value)}
        onPressEnter={onSendWhatsApp}
        className="h-8 text-xs w-full"
        allowClear
      />
      <div className="flex gap-1.5 items-center flex-wrap pt-1">
        {onSendWhatsApp && (
          <Tooltip title={whatsappDisabledTooltip}>
            <span className="flex-1">
              <Button
                type="primary"
                size="small"
                loading={isSendWhatsAppPending}
                onClick={onSendWhatsApp}
                className="text-xs font-semibold h-8 bg-green-600 hover:bg-green-500 rounded w-full"
                disabled={isWhatsAppButtonDisabled}
              >
                Send WhatsApp
              </Button>
            </span>
          </Tooltip>
        )}
        {onSendWhatsAppLink && (
          <Tooltip title={whatsappDisabledTooltip}>
            <span>
              <Button
                size="small"
                loading={isSendWhatsAppLinkPending}
                onClick={onSendWhatsAppLink}
                icon={<WhatsAppOutlined className="text-green-500" />}
                className="text-xs font-semibold h-8 rounded"
                disabled={isWhatsAppButtonDisabled}
              >
                Link
              </Button>
            </span>
          </Tooltip>
        )}
        {onSendSMSLink && (
          <Button
            size="small"
            loading={isSendSmsLinkPending}
            onClick={onSendSMSLink}
            icon={<MessageOutlined className="text-blue-500" />}
            className="text-xs font-semibold h-8 rounded"
            disabled={!isSmsGatewayEnable}
          >
            SMS
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-2 w-full py-5">
      <div className="flex flex-wrap gap-2 w-full">
        {extraActions}

        {canPrint && handlePrint && (
          <Button
            type="primary"
            size="middle"
            onClick={handlePrint}
            className="flex-grow min-w-[100px] text-xs font-semibold h-9 rounded-md"
          >
            Print Invoice
          </Button>
        )}

        {canDownload && handleDownload && (
          <Button
            size="middle"
            onClick={handleDownload}
            className="flex-grow min-w-[80px] text-xs font-semibold h-9 rounded-md"
          >
            Download
          </Button>
        )}

        {canCourier && handleCourier && (
          <Button
            size="middle"
            icon={<PrinterOutlined />}
            onClick={handleCourier}
            className="flex-grow min-w-[100px] text-xs font-semibold h-9 rounded-md"
          >
            Courier Print
          </Button>
        )}

        {canShare && useSharePopover && (
          <Popover
            content={sharePopoverContent}
            trigger="click"
            placement="topRight"
          >
            <Button
              size="middle"
              icon={<ShareAltOutlined className="text-green-600" />}
              className="flex-grow min-w-[100px] text-xs font-semibold h-9 rounded-md border-green-200 text-green-700 hover:bg-green-50"
            >
              Send / Share
            </Button>
          </Popover>
        )}
      </div>

      {canShare && !useSharePopover && (
        <div className="flex flex-wrap lg:flex-nowrap gap-2 items-center border-t border-gray-150 pt-2 w-full">
          <Input
            placeholder="WhatsApp number (e.g. 0712345678)"
            prefix={<SendOutlined className="text-gray-400" />}
            value={effectivePhone}
            onChange={(e) => onWhatsappPhoneChange?.(e.target.value)}
            onPressEnter={onSendWhatsApp}
            className="h-9 text-xs flex-grow w-full lg:w-auto"
            allowClear
          />
          {onSendWhatsApp && (
            <Tooltip title={whatsappDisabledTooltip}>
              <span className="flex-grow lg:flex-none">
                <Button
                  type="primary"
                  size="middle"
                  loading={isSendWhatsAppPending}
                  onClick={onSendWhatsApp}
                  className="text-xs font-semibold h-9 rounded-md bg-green-600 w-full"
                  disabled={isWhatsAppButtonDisabled}
                >
                  Send Invoice
                </Button>
              </span>
            </Tooltip>
          )}
          {onSendWhatsAppLink && (
            <Tooltip title={whatsappDisabledTooltip}>
              <span className="flex-grow lg:flex-none">
                <Button
                  type="default"
                  size="middle"
                  loading={isSendWhatsAppLinkPending}
                  onClick={onSendWhatsAppLink}
                  icon={<WhatsAppOutlined className="text-green-500" />}
                  className="text-xs font-semibold h-9 rounded-md flex items-center justify-center w-full"
                  disabled={isWhatsAppButtonDisabled}
                >
                  Send Link
                </Button>
              </span>
            </Tooltip>
          )}
          {onSendSMSLink && (
            <Button
              type="default"
              size="middle"
              loading={isSendSmsLinkPending}
              onClick={onSendSMSLink}
              icon={<MessageOutlined className="text-blue-500" />}
              className="text-xs font-semibold h-9 rounded-md flex items-center justify-center flex-grow lg:flex-none"
              disabled={!isSmsGatewayEnable}
            >
              Send Link
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
